import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { SITE } from "@/constants/site";

const stats = [
  { k: "04", v: "Pillars" },
  { k: "01", v: "Mission" },
  { k: "∞", v: "Reps" },
];

const About = () => {
  useEffect(() => {
    document.title = "TWGAS · About";
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="bg-[#0A0A0A]">
      {/* Header */}
      <section className="pt-40 md:pt-56 pb-20 border-b border-white/10">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-8">
            <div className="num-tag mb-4">/ ABOUT · THE STORY</div>
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-display uppercase text-6xl md:text-8xl lg:text-[9rem] leading-[0.86] tracking-tight"
            >
              We build
              <br />
              <span className="stroke-fire">people first.</span>
            </motion.h1>
          </div>
          <div className="md:col-span-4 flex items-end">
            <img
              src={SITE.logo}
              alt="TWGAS crest"
              className="w-40 h-40 md:w-52 md:h-52 md:ml-auto"
            />
          </div>
        </div>
      </section>

      {/* Ethos */}
      <section className="py-24 md:py-32 border-b border-white/10">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="num-tag mb-4">/ 01 · ETHOS</div>
            <h2 className="font-display uppercase text-4xl md:text-5xl leading-[0.95] tracking-tight">
              The Work Gone
              <br />
              Always Show.
            </h2>
          </div>
          <div className="md:col-span-7 space-y-6 text-zinc-300 text-lg leading-relaxed max-w-[62ch]">
            <p>
              TWGAS Athletics &amp; Development LLC exists to help
              student-athletes become more than athletes. We stack sport, school,
              character, brand, and financial literacy into one system, taught
              by people who've walked it.
            </p>
            <p>
              We don't sell hype. We build habits. Position-specific training,
              film sessions, life coaching, and NIL strategy — every piece
              engineered to compound.
            </p>
            <p className="text-zinc-500">
              Prepare. Perform. Position. Profit.
            </p>
          </div>
        </div>
      </section>

      {/* Numbers */}
      <section className="py-20 border-b border-white/10">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10 grid grid-cols-3 gap-6">
          {stats.map((s, i) => (
            <motion.div
              key={s.v}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.08 * i }}
              className="border-t border-white/10 pt-6"
            >
              <div className="font-display text-6xl md:text-8xl leading-none text-[#FF4D00]">
                {s.k}
              </div>
              <div className="mt-3 font-mono-plex text-[11px] tracking-[0.28em] uppercase text-zinc-400">
                {s.v}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="py-24 md:py-32 border-b border-white/10">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-6">
            <div className="num-tag mb-4">/ 02 · MISSION</div>
            <h3 className="font-display uppercase text-4xl md:text-5xl leading-[0.95] tracking-tight">
              Better athletes.
              <br />
              Better people.
            </h3>
            <p className="mt-6 text-zinc-400 leading-relaxed max-w-[52ch]">
              Everything we do points back at one goal — help athletes succeed
              in sports, school, and life. Not just during the playing career.
              For life.
            </p>
          </div>
          <div className="md:col-span-6">
            <div className="num-tag mb-4">/ 03 · VISION</div>
            <h3 className="font-display uppercase text-4xl md:text-5xl leading-[0.95] tracking-tight">
              Bigger
              <br />
              <span className="stroke-fire">opportunities.</span>
            </h3>
            <p className="mt-6 text-zinc-400 leading-relaxed max-w-[52ch]">
              A world where every serious athlete has access to the same
              development, mentorship, and monetization tools the top 1% take
              for granted.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
          <h3 className="font-display uppercase text-5xl md:text-7xl leading-[0.9] tracking-tight">
            Your journey.
            <br />
            <span className="stroke-fire">Our mission.</span>
          </h3>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact" className="btn-fire">
              Get Started
              <ArrowUpRight size={18} />
            </Link>
            <a
              href={SITE.instagramUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost"
            >
              Instagram
              <Instagram size={16} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
